import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, EmptyRow, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { BASE, DeviceArray, SrPairingCode, keys } from './api';
import type { SrBuilding } from './api';

/** Shared tablets of the building, and the one-off pairing code for a new one. */
export function DevicesTab({ building, canManage }: { building: SrBuilding; canManage: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [code, setCode] = useState<SrPairingCode | null>(null);
  const q = useQuery({ queryKey: keys.devices(building.id), queryFn: () => api.get(`${BASE}/${building.id}/devices`, { schema: DeviceArray }) });
  const create = useMutation({
    mutationFn: () => api.post(`${BASE}/${building.id}/pairing-codes`, { body: { name }, schema: SrPairingCode }),
    onSuccess: (c) => {
      setCode(c);
      setName('');
    },
  });
  const unpair = useMutation({
    mutationFn: (deviceId: string) => api.delete(`${BASE}/${building.id}/devices/${deviceId}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['shared-reception', 'platform'] });
      toast(t('srp.dev.unpaired'));
    },
  });
  const errs = fieldErrors(create.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  const zone = building.timeZone;
  return (
    <div className="space-y-6">
      {canManage && building.status === 'active' ? (
        <Card title={t('srp.dev.pairing')}>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-3 sm:items-end" noValidate>
            <TextField label={t('srp.dev.pairingName')} hint={t('srp.dev.pairingNameHint')} value={name} onChange={(e) => setName(e.target.value)} error={errs['name']} required />
            <div>
              <Button type="submit" variant="primary" disabled={create.isPending || !name.trim()}>
                {t('srp.dev.pairingCreate')}
              </Button>
            </div>
          </form>
          <ErrorLine error={Object.keys(errs).length ? null : create.error} />
          {code ? (
            <div className="mt-4" role="status">
              <Alert kind="success">
                {t('srp.dev.pairingShown', { time: formatDateTime(code.expiresAt, zone) })}
                <span className="mt-2 block text-2xl font-bold tracking-widest" aria-label={t('srp.dev.pairingCode')}>
                  {code.code.replace(/(\d{3})(?=\d)/g, '$1 ')}
                </span>
              </Alert>
            </div>
          ) : null}
        </Card>
      ) : null}
      <ErrorLine error={unpair.error} />
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <TableWrap caption={t('srp.dev.table')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('srp.dev.col.name')}</Th>
              <Th>{t('srp.dev.col.status')}</Th>
              <Th>{t('srp.dev.col.seen')}</Th>
              <Th>{t('srp.dev.col.version')}</Th>
              <Th>{t('srp.dev.col.battery')}</Th>
              <Th>{t('srp.dev.col.links')}</Th>
              {canManage ? <Th>{t('srp.col.actions')}</Th> : null}
            </tr>
          </thead>
          <tbody>
            {q.data.length === 0 ? <EmptyRow cols={canManage ? 7 : 6} /> : null}
            {q.data.map((d) => (
              <tr key={d.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium">{d.name}</td>
                <td className="px-4 py-3">
                  {d.status === 'revoked' ? <Badge>{t('srp.dev.revoked')}</Badge> : <Badge tone={d.online ? 'good' : 'warn'}>{d.online ? t('srp.dev.online') : t('srp.dev.offline')}</Badge>}
                </td>
                <td className="px-4 py-3">{formatDateTime(d.lastSeenAt, zone)}</td>
                <td className="px-4 py-3">{d.appVersion ?? ''}</td>
                <td className="px-4 py-3">
                  {d.health?.batteryPercent != null ? `${d.health.batteryPercent}%${d.health.charging ? ` (${t('srp.dev.charging')})` : ''}` : ''}
                </td>
                <td className="px-4 py-3">{d.links.length === 0 ? t('srp.dev.noLinks') : d.links.map((l) => l.displayName ?? '').filter(Boolean).join(', ')}</td>
                {canManage ? (
                  <td className="px-4 py-3">
                    {d.status === 'active' ? (
                      <Button variant="danger" onClick={() => unpair.mutate(d.id)} disabled={unpair.isPending} aria-label={t('srp.dev.unpairNamed', { name: d.name })}>
                        {t('srp.dev.unpair')}
                      </Button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </div>
  );
}
